import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@cedarjs/web'
import {
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SecurityIcon from '@mui/icons-material/Security'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import FlightIcon from '@mui/icons-material/Flight'
import { INTEGRATION_REGISTRATIONS_QUERY } from 'src/pages/AdminIntegrationsPage/queries/integrationRegistrations'
import { INTEGRATIONS_QUERY } from 'src/pages/AdminIntegrationsPage/queries/integrations'

const ALL_SECRETS_QUERY = gql`
  query FindAllSecrets {
    allSecrets {
      id
      scope
      orgSlug
      entityType
      entityId
      description
      metadata
      createdAt
      updatedAt
      createdBy {
        id
        firstName
        lastName
        email
      }
      updatedBy {
        id
        firstName
        lastName
        email
      }
    }
  }
`

const SECRETS_QUERY = gql`
  query FindSecrets(
    $scope: SecretScope!
    $orgSlug: String
    $entityType: EntityType!
    $entityId: String!
  ) {
    secrets(
      scope: $scope
      orgSlug: $orgSlug
      entityType: $entityType
      entityId: $entityId
    ) {
      id
      scope
      orgSlug
      entityType
      entityId
      description
      metadata
      createdAt
      updatedAt
      createdBy {
        id
        firstName
        lastName
        email
      }
      updatedBy {
        id
        firstName
        lastName
        email
      }
    }
  }
`

const SET_SECRET_MUTATION = gql`
  mutation SetSecret($input: SetSecretInput!) {
    setSecret(input: $input) {
      id
      scope
      orgSlug
      entityType
      entityId
      description
      metadata
      createdAt
      updatedAt
      createdBy {
        id
        firstName
        lastName
        email
      }
      updatedBy {
        id
        firstName
        lastName
        email
      }
    }
  }
`

interface SecretData {
  id: string
  scope: string
  orgSlug: string | null
  entityType: string
  entityId: string
  description: string | null
  metadata: Record<string, unknown>
  createdAt: string
  updatedAt: string
  createdBy: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string
  }
  updatedBy: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string
  }
}

interface IntegrationWithSecrets {
  id: string
  name: string
  displayName: string
  description: string | null
  secrets: SecretData[]
  secretCount: number
}

interface RegistrationWithSecrets {
  id: string
  aircraft: {
    id: string
    tailNumber: string
    name: string
    orgSlug: string
  }
  integration: {
    id: string
    name: string
    displayName: string
  }
  secrets: SecretData[]
  secretCount: number
}

const IntegrationSecretsManager = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [selectedType, setSelectedType] = useState<
    'integration' | 'organization' | 'registration'
  >('integration')
  const [selectedIntegration, setSelectedIntegration] = useState('')
  const [selectedOrganization, setSelectedOrganization] = useState('')
  const [selectedRegistration, setSelectedRegistration] = useState('')
  const [secretValue, setSecretValue] = useState('')
  const [description, setDescription] = useState('')
  const [jsonError, setJsonError] = useState('')
  const [existingSecrets, setExistingSecrets] = useState<SecretData[]>([])

  const { data: integrationsData } = useQuery(INTEGRATIONS_QUERY)
  const { data: registrationsData } = useQuery(
    INTEGRATION_REGISTRATIONS_QUERY,
    {
      variables: {
        aircraftsOrgSlug: 'all',
      },
    }
  )
  const { data: allSecretsData, refetch: refetchAllSecrets } =
    useQuery(ALL_SECRETS_QUERY)

  const [setSecret] = useMutation(SET_SECRET_MUTATION, {
    onCompleted: () => {
      setIsAddDialogOpen(false)
      resetForm()
      refetchAllSecrets()
      // Refresh existing secrets
      if (selectedType === 'integration' && selectedIntegration) {
        fetchExistingSecrets(selectedIntegration, null, 'INTEGRATION')
      } else if (
        selectedType === 'organization' &&
        selectedIntegration &&
        selectedOrganization
      ) {
        fetchExistingSecrets(
          selectedIntegration,
          selectedOrganization,
          'INTEGRATION'
        )
      } else if (selectedType === 'registration' && selectedRegistration) {
        const registration = registrationsData?.integrationRegistrations.find(
          (reg) => reg.id === selectedRegistration
        )
        if (registration) {
          fetchExistingSecrets(
            selectedRegistration,
            registration.aircraft.orgSlug,
            'INTEGRATION_REGISTRATION'
          )
        }
      }
    },
  })

  // Get unique organizations from registrations
  const organizations = React.useMemo(() => {
    if (!registrationsData?.integrationRegistrations) return []

    const orgSet = new Set<string>()
    registrationsData.integrationRegistrations.forEach((reg) => {
      orgSet.add(reg.aircraft.orgSlug)
    })

    return Array.from(orgSet).sort()
  }, [registrationsData])

  // Process secrets data to group by integrations and registrations
  const integrationsWithSecrets: IntegrationWithSecrets[] =
    React.useMemo(() => {
      if (!integrationsData?.integrations || !allSecretsData?.allSecrets)
        return []

      return integrationsData.integrations
        .map((integration) => {
          const integrationSecrets = allSecretsData.allSecrets.filter(
            (secret) =>
              secret.scope === 'INTEGRATIONS' &&
              secret.entityType === 'INTEGRATION' &&
              secret.entityId === integration.id &&
              secret.orgSlug === null // Global secrets have null orgSlug
          )

          return {
            ...integration,
            secrets: integrationSecrets,
            secretCount: integrationSecrets.length,
          }
        })
        .filter((integration) => integration.secretCount > 0)
    }, [integrationsData, allSecretsData])

  const organizationsWithSecrets: Array<{
    orgSlug: string
    integrationId: string
    integrationName: string
    secrets: SecretData[]
    secretCount: number
  }> = React.useMemo(() => {
    if (!integrationsData?.integrations || !allSecretsData?.allSecrets)
      return []

    const orgSecrets: Record<
      string,
      Array<{
        integrationId: string
        integrationName: string
        secrets: SecretData[]
      }>
    > = {}

    integrationsData.integrations.forEach((integration) => {
      const orgLevelSecrets = allSecretsData.allSecrets.filter(
        (secret) =>
          secret.scope === 'INTEGRATIONS' &&
          secret.entityType === 'INTEGRATION' &&
          secret.entityId === integration.id &&
          secret.orgSlug !== null // Organization secrets have orgSlug
      )

      orgLevelSecrets.forEach((secret) => {
        if (secret.orgSlug) {
          if (!orgSecrets[secret.orgSlug]) {
            orgSecrets[secret.orgSlug] = []
          }

          const existingEntry = orgSecrets[secret.orgSlug].find(
            (entry) => entry.integrationId === integration.id
          )

          if (existingEntry) {
            existingEntry.secrets.push(secret)
          } else {
            orgSecrets[secret.orgSlug].push({
              integrationId: integration.id,
              integrationName: integration.displayName || integration.name,
              secrets: [secret],
            })
          }
        }
      })
    })

    return Object.entries(orgSecrets)
      .map(([orgSlug, integrations]) => {
        const totalSecrets = integrations.reduce(
          (sum, integration) => sum + integration.secrets.length,
          0
        )
        return {
          orgSlug,
          integrationId: integrations[0].integrationId,
          integrationName: integrations[0].integrationName,
          secrets: integrations.flatMap((integration) => integration.secrets),
          secretCount: totalSecrets,
        }
      })
      .filter((org) => org.secretCount > 0)
  }, [integrationsData, allSecretsData])

  const registrationsWithSecrets: RegistrationWithSecrets[] =
    React.useMemo(() => {
      if (
        !registrationsData?.integrationRegistrations ||
        !allSecretsData?.allSecrets
      )
        return []

      return registrationsData.integrationRegistrations
        .map((registration) => {
          const registrationSecrets = allSecretsData.allSecrets.filter(
            (secret) =>
              secret.scope === 'INTEGRATIONS' &&
              secret.entityType === 'INTEGRATION_REGISTRATION' &&
              secret.entityId === registration.id &&
              secret.orgSlug === registration.aircraft.orgSlug
          )

          return {
            ...registration,
            secrets: registrationSecrets,
            secretCount: registrationSecrets.length,
          }
        })
        .filter((registration) => registration.secretCount > 0)
    }, [registrationsData, allSecretsData])

  const fetchExistingSecrets = async (
    entityId: string,
    orgSlug: string | null,
    entityType: 'INTEGRATION' | 'INTEGRATION_REGISTRATION'
  ) => {
    try {
      const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query FindSecrets($scope: SecretScope!, $orgSlug: String, $entityType: EntityType!, $entityId: String!) {
              secrets(scope: $scope, orgSlug: $orgSlug, entityType: $entityType, entityId: $entityId) {
                id
                scope
                orgSlug
                entityType
                entityId
                description
                metadata
                createdAt
                updatedAt
                createdBy {
                  id
                  firstName
                  lastName
                  email
                }
                updatedBy {
                  id
                  firstName
                  lastName
                  email
                }
              }
            }
          `,
          variables: {
            scope: 'INTEGRATIONS',
            orgSlug,
            entityType,
            entityId,
          },
        }),
      })

      const result = await response.json()
      if (result.data?.secrets) {
        setExistingSecrets(result.data.secrets)
      }
    } catch (error) {
      console.error('Error fetching secrets:', error)
    }
  }

  const resetForm = () => {
    setSelectedType('integration')
    setSelectedIntegration('')
    setSelectedOrganization('')
    setSelectedRegistration('')
    setSecretValue('')
    setDescription('')
    setJsonError('')
    setExistingSecrets([])
  }

  const handleAddSecret = () => {
    if (!secretValue) return

    // Validate JSON
    try {
      JSON.parse(secretValue)
    } catch (error) {
      setJsonError('Invalid JSON format')
      return
    }

    let entityType: 'INTEGRATION' | 'INTEGRATION_REGISTRATION'
    let entityId: string
    let orgSlug: string | null = null

    if (selectedType === 'integration') {
      entityType = 'INTEGRATION'
      entityId = selectedIntegration
      // Global secrets have null orgSlug
    } else if (selectedType === 'organization') {
      entityType = 'INTEGRATION'
      entityId = selectedIntegration
      orgSlug = selectedOrganization
    } else {
      entityType = 'INTEGRATION_REGISTRATION'
      entityId = selectedRegistration
      // Get orgSlug from the selected registration's aircraft
      const registration = registrationsData?.integrationRegistrations.find(
        (reg) => reg.id === selectedRegistration
      )
      orgSlug = registration?.aircraft.orgSlug || null
    }

    setSecret({
      variables: {
        input: {
          scope: 'INTEGRATIONS',
          orgSlug,
          entityType,
          entityId,
          value: secretValue,
          description,
        },
      },
    })
  }

  const handleOpenAddDialog = () => {
    resetForm()
    setIsAddDialogOpen(true)
  }

  const handleJsonChange = (value: string) => {
    setSecretValue(value)
    setJsonError('')
    try {
      if (value) {
        JSON.parse(value)
      }
    } catch (error) {
      setJsonError('Invalid JSON format')
    }
  }

  const handleIntegrationChange = (integrationId: string) => {
    setSelectedIntegration(integrationId)
    if (integrationId) {
      if (selectedType === 'integration') {
        fetchExistingSecrets(integrationId, null, 'INTEGRATION')
      } else if (selectedType === 'organization' && selectedOrganization) {
        fetchExistingSecrets(integrationId, selectedOrganization, 'INTEGRATION')
      }
    } else {
      setExistingSecrets([])
    }
  }

  const handleOrganizationChange = (orgSlug: string) => {
    setSelectedOrganization(orgSlug)
    if (orgSlug && selectedIntegration) {
      fetchExistingSecrets(selectedIntegration, orgSlug, 'INTEGRATION')
    } else {
      setExistingSecrets([])
    }
  }

  const handleRegistrationChange = (registrationId: string) => {
    setSelectedRegistration(registrationId)
    if (registrationId) {
      const registration = registrationsData?.integrationRegistrations.find(
        (reg) => reg.id === registrationId
      )
      if (registration) {
        fetchExistingSecrets(
          registrationId,
          registration.aircraft.orgSlug,
          'INTEGRATION_REGISTRATION'
        )
      }
    } else {
      setExistingSecrets([])
    }
  }

  const selectedIntegrationData = integrationsData?.integrations.find(
    (i) => i.id === selectedIntegration
  )

  const selectedRegistrationData =
    registrationsData?.integrationRegistrations.find(
      (reg) => reg.id === selectedRegistration
    )

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ mr: 1 }} />
        <Typography variant="h6">Integration Secrets Management</Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Integration Secret
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Add JSON secrets for integrations at different levels:
          <br />• <strong>Global Integration Secret:</strong> All organizations
          use the same secret (stored at integration level)
          <br />• <strong>Organization Integration Secret:</strong> All aircraft
          in a specific organization use the same secret (stored at integration
          level with orgSlug)
          <br />• <strong>Aircraft Registration Secret:</strong> Specific
          aircraft registration uses its own secret (stored at registration
          level)
        </Typography>
      </Alert>

      {/* Overview of Integrations with Secrets */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IntegrationInstructionsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Global Integration Secrets ({integrationsWithSecrets.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {integrationsWithSecrets.length === 0 ? (
            <Typography color="text.secondary">
              No integrations have global secrets configured.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {integrationsWithSecrets.map((integration) => (
                <Grid item xs={12} md={6} key={integration.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6">
                          {integration.displayName || integration.name}
                        </Typography>
                        <Chip
                          label={`${integration.secretCount} secret${
                            integration.secretCount !== 1 ? 's' : ''
                          }`}
                          size="small"
                          color="primary"
                        />
                      </Box>
                      {integration.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {integration.description}
                        </Typography>
                      )}
                      <List dense>
                        {integration.secrets.map((secret) => (
                          <ListItem key={secret.id} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <VisibilityOffIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${secret.entityType} Secret`}
                              secondary={secret.description || 'No description'}
                            />
                            <Chip label="Global" size="small" color="primary" />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Overview of Organizations with Secrets */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Organization Integration Secrets (
              {organizationsWithSecrets.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {organizationsWithSecrets.length === 0 ? (
            <Typography color="text.secondary">
              No organizations have integration secrets configured.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {organizationsWithSecrets.map((org) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={`${org.orgSlug}-${org.integrationId}`}
                >
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6">{org.orgSlug}</Typography>
                        <Chip
                          label={`${org.secretCount} secret${
                            org.secretCount !== 1 ? 's' : ''
                          }`}
                          size="small"
                          color="warning"
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        <strong>Integration:</strong> {org.integrationName}
                      </Typography>
                      <List dense>
                        {org.secrets.map((secret) => (
                          <ListItem key={secret.id} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <VisibilityOffIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${secret.entityType} Secret`}
                              secondary={secret.description || 'No description'}
                            />
                            <Chip
                              label="Organization"
                              size="small"
                              color="warning"
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Overview of Integration Registrations with Secrets */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FlightIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Aircraft Registration Secrets ({registrationsWithSecrets.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {registrationsWithSecrets.length === 0 ? (
            <Typography color="text.secondary">
              No aircraft registrations have entity-level secrets configured.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {registrationsWithSecrets.map((registration) => (
                <Grid item xs={12} md={6} key={registration.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6">
                          {registration.aircraft.tailNumber}
                        </Typography>
                        <Chip
                          label={`${registration.secretCount} secret${
                            registration.secretCount !== 1 ? 's' : ''
                          }`}
                          size="small"
                          color="secondary"
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        <strong>Integration:</strong>{' '}
                        {registration.integration.displayName ||
                          registration.integration.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        <strong>Organization:</strong>{' '}
                        {registration.aircraft.orgSlug}
                      </Typography>
                      <List dense>
                        {registration.secrets.map((secret) => (
                          <ListItem key={secret.id} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <VisibilityOffIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${secret.entityType} Secret`}
                              secondary={secret.description || 'No description'}
                            />
                            <Chip
                              label="Registration"
                              size="small"
                              color="secondary"
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Integration Secret</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Secret Type</InputLabel>
              <Select
                value={selectedType}
                label="Secret Type"
                onChange={(e) =>
                  setSelectedType(
                    e.target.value as
                      | 'integration'
                      | 'organization'
                      | 'registration'
                  )
                }
              >
                <MenuItem value="integration">
                  Global Integration Secret
                </MenuItem>
                <MenuItem value="organization">
                  Organization Integration Secret
                </MenuItem>
                <MenuItem value="registration">
                  Aircraft Registration Secret
                </MenuItem>
              </Select>
            </FormControl>

            {selectedType === 'integration' && (
              <FormControl fullWidth>
                <InputLabel>Integration</InputLabel>
                <Select
                  value={selectedIntegration}
                  label="Integration"
                  onChange={(e) => handleIntegrationChange(e.target.value)}
                >
                  {integrationsData?.integrations.map((integration) => (
                    <MenuItem key={integration.id} value={integration.id}>
                      {integration.displayName || integration.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedType === 'organization' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Integration</InputLabel>
                  <Select
                    value={selectedIntegration}
                    label="Integration"
                    onChange={(e) => handleIntegrationChange(e.target.value)}
                  >
                    {integrationsData?.integrations.map((integration) => (
                      <MenuItem key={integration.id} value={integration.id}>
                        {integration.displayName || integration.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={selectedOrganization}
                    label="Organization"
                    onChange={(e) => handleOrganizationChange(e.target.value)}
                  >
                    {organizations.map((orgSlug) => (
                      <MenuItem key={orgSlug} value={orgSlug}>
                        {orgSlug}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {selectedType === 'registration' && (
              <FormControl fullWidth>
                <InputLabel>Integration Registration</InputLabel>
                <Select
                  value={selectedRegistration}
                  label="Integration Registration"
                  onChange={(e) => handleRegistrationChange(e.target.value)}
                >
                  {registrationsData?.integrationRegistrations.map(
                    (registration) => (
                      <MenuItem key={registration.id} value={registration.id}>
                        {registration.integration.displayName ||
                          registration.integration.name}{' '}
                        - {registration.aircraft.tailNumber} (
                        {registration.aircraft.orgSlug})
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            )}

            {selectedIntegrationData && selectedType === 'integration' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Integration
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong>{' '}
                    {selectedIntegrationData.displayName ||
                      selectedIntegrationData.name}
                  </Typography>
                  {selectedIntegrationData.description && (
                    <Typography variant="body2">
                      <strong>Description:</strong>{' '}
                      {selectedIntegrationData.description}
                    </Typography>
                  )}
                  <Chip
                    label="Global Level"
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            )}

            {selectedIntegrationData &&
              selectedType === 'organization' &&
              selectedOrganization && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Organization Integration
                    </Typography>
                    <Typography variant="body2">
                      <strong>Integration:</strong>{' '}
                      {selectedIntegrationData.displayName ||
                        selectedIntegrationData.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Organization:</strong> {selectedOrganization}
                    </Typography>
                    <Chip
                      label="Organization Level"
                      size="small"
                      color="warning"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              )}

            {selectedRegistrationData && selectedType === 'registration' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Registration
                  </Typography>
                  <Typography variant="body2">
                    <strong>Integration:</strong>{' '}
                    {selectedRegistrationData.integration.displayName ||
                      selectedRegistrationData.integration.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Aircraft:</strong>{' '}
                    {selectedRegistrationData.aircraft.tailNumber} (
                    {selectedRegistrationData.aircraft.orgSlug})
                  </Typography>
                  <Chip
                    label="Entity Level"
                    size="small"
                    color="secondary"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            )}

            {existingSecrets.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Secrets
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Entity ID</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Updated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingSecrets.map((secret) => (
                          <TableRow key={secret.id}>
                            <TableCell>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <VisibilityOffIcon
                                  sx={{ mr: 1, fontSize: 16 }}
                                />
                                {secret.entityId}
                              </Box>
                            </TableCell>
                            <TableCell>{secret.description || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  secret.entityType === 'INTEGRATION'
                                    ? 'Global'
                                    : secret.entityType ===
                                      'INTEGRATION_REGISTRATION'
                                    ? 'Registration'
                                    : 'Organization'
                                }
                                size="small"
                                color={
                                  secret.entityType === 'INTEGRATION'
                                    ? 'primary'
                                    : secret.entityType ===
                                      'INTEGRATION_REGISTRATION'
                                    ? 'secondary'
                                    : 'warning'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(secret.updatedAt).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            <TextField
              label="Secret Value (JSON)"
              value={secretValue}
              onChange={(e) => handleJsonChange(e.target.value)}
              placeholder='{"username": "user", "password": "pass", "apiKey": "key123"}'
              multiline
              rows={6}
              fullWidth
              error={!!jsonError}
              helperText={jsonError || 'Enter valid JSON for the secret value'}
            />

            <TextField
              label="Description (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this secret is for"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddSecret}
            variant="contained"
            disabled={
              !secretValue ||
              !!jsonError ||
              (selectedType === 'integration' && !selectedIntegration) ||
              (selectedType === 'organization' &&
                (!selectedIntegration || !selectedOrganization)) ||
              (selectedType === 'registration' && !selectedRegistration)
            }
          >
            Add Secret
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default IntegrationSecretsManager
